import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { DTConnectionSpeedIcon } from '@icons';

import ProgressBar from '../ProgressBar';
import { useFastCheckInternetConnectionSpeed } from './hooks';
import { useStyles } from './styles';
import { env } from 'env';
import LoadingProgress from '@components/LoadingProgress';
import { useCheckServiceLatency } from '@features/serviceLatency/hooks/useCheckServiceLatency';
import HelperBox from '../HelperBox';

const ServiceLatency = lazy(
  () => import('@features/serviceLatency/components/ServiceLatency'),
);

const { REACT_APP_FEATURE_SERVER_LATENCY } = env;
const showServerLatency = REACT_APP_FEATURE_SERVER_LATENCY === 'true';

const ConnectionTest = () => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [isShowLatencyNotification, setIsShowLatencyNotification] =
    useState(false);

  const { isCompleted: isLatencyTestCompleted, serviceLatency } =
    useCheckServiceLatency(true);
  const {
    isCompleted: isSpeedTestCompleted,
    isShowSpeedTestNotification,
    speed,
  } = useFastCheckInternetConnectionSpeed();

  const progressBarSpeed = speed > 100 ? 100 : speed;

  useEffect(() => {
    if (
      isSpeedTestCompleted &&
      !isShowSpeedTestNotification &&
      isLatencyTestCompleted
    ) {
      if (serviceLatency > +(env.REACT_APP_SPEED_CHECK_MAX_LATENCY || 500)) {
        setIsShowLatencyNotification(true);
      }
    }
  }, [
    isSpeedTestCompleted,
    isShowSpeedTestNotification,
    isLatencyTestCompleted,
    serviceLatency,
  ]);

  return (
    <Box>
      <Box sx={classes.root}>
        <Box sx={classes.section}>
          <ProgressBar
            value={progressBarSpeed}
            isCompleted={isSpeedTestCompleted}
          />
          <Box sx={classes.textContent}>
            <Box sx={classes.iconAndText}>
              <DTConnectionSpeedIcon sx={classes.icon} />
              <Typography sx={classes.text}>
                {translate('devicesTest.connectionTest.connectionSpeed')}
              </Typography>
            </Box>
            <Typography sx={classes.speed}>{speed} Mb/s</Typography>
          </Box>
        </Box>
        <Suspense fallback={<LoadingProgress />}>
          {showServerLatency && (
            <ServiceLatency
              serviceLatency={serviceLatency}
              isServiceLatencyTestCompleted={isLatencyTestCompleted}
              showNotificationMessage={isShowLatencyNotification}
            />
          )}
        </Suspense>
      </Box>
      <HelperBox
        title={translate('devicesTest.connectionTest.helper.title')}
        text={translate('devicesTest.connectionTest.helper.text')}
      />
    </Box>
  );
};

export default ConnectionTest;
